/**
 * „Nowe pytanie" — KANON `05-edytor-dodaj-pytanie.html`, in place at the foot of „Pytania własne".
 *
 * Two ways in, one segmented control: write a question, or lift one off the shelf. Picking a shelf
 * entry inserts a COPY carrying its `sourceQuestionId`, which is what lets the catalog say later
 * which surveys use it without the survey ever reading back through the shelf.
 *
 * The fields themselves are `QuestionFields`, shared with the shelf's own dialog — the two surfaces
 * collect the same thing and the backend refuses the same things for both. What lives here is the
 * card: where it stands, how a question is sourced, and what „Dodaj pytanie" does with it.
 *
 * A recording goes to the exercise picker rather than straight into the list: it has to name a
 * movement before it exists, and `CustomQuestionRules` refuses one that names none.
 */

import { useState } from 'react';

import { SearchField } from '~/shared/ui/SearchField';
import ember from '~/shared/ui/emberButton.module.css';

import type { AuthoredQuestion } from '../model/editorDraft';
import { filterShelf, type ShelfQuestion } from '../model/questionCatalog';
import {
  EMPTY_QUESTION_FIELDS,
  normalizedOptions,
  questionFieldsComplete,
  type QuestionFieldsValue,
} from '../model/questionFields';
import type { TranslateKey } from '../model/translateKey';
import { answerTypeIcon } from './AnswerTypePill';
import { QuestionFields } from './QuestionFields';
import { BookmarkIcon, PencilIcon } from '~/shared/ui/ReportIcons';
import seg from '~/shared/ui/segmented.module.css';
import styles from './EditorForm.module.css';

export interface NewQuestionCardProps {
  /** The trainer's shelf, already read by the editor. Empty is a real answer, not a wait. */
  readonly shelf: readonly ShelfQuestion[];
  /** How many recordings this survey still has room for. */
  /** The recording type is offered only where the survey can hold one — never on a lead form. */
  readonly allowClip: boolean;
  readonly clipsLeft: number;
  readonly onPickExercise: (question: AuthoredQuestion) => void;
  readonly onAdd: (question: AuthoredQuestion) => void;
  readonly onPickShelf: (question: ShelfQuestion) => void;
  readonly onCancel: () => void;
  readonly t: TranslateKey;
}

export function NewQuestionCard({
  shelf,
  allowClip,
  clipsLeft,
  onPickExercise,
  onAdd,
  onPickShelf,
  onCancel,
  t,
}: NewQuestionCardProps) {
  const [source, setSource] = useState<'own' | 'shelf'>('own');
  const [value, setValue] = useState<QuestionFieldsValue>(EMPTY_QUESTION_FIELDS);
  const [term, setTerm] = useState('');

  const complete = questionFieldsComplete(value, { clipsLeft });

  const submit = (): void => {
    if (!complete) return;
    const question: AuthoredQuestion = {
      label: value.label.trim(),
      answerType: value.answerType,
      options: normalizedOptions(value),
      required: value.required,
      exerciseId: null,
      sourceQuestionId: null,
    };
    if (value.answerType === 'ExerciseClip') {
      onPickExercise(question);
      return;
    }
    onAdd(question);
  };

  return (
    <div className={styles.newCard} role="group" aria-label={t('web:reports.editor.newQuestion.title')}>
      <div className={styles.newHead}>
        <h3 className={styles.newTitle}>{t('web:reports.editor.newQuestion.title')}</h3>
        {/* The canon puts a glyph on each option — a pencil on „Napisz własne", a FILLED bookmark
          * on „Z katalogu". The filled mark is the same one a question already on the shelf wears
          * on its row, which is what makes the two readable as one idea. */}
        <div
          className={seg.seg}
          role="group"
          aria-label={t('web:reports.editor.newQuestion.sourceLabel')}
        >
          <button
            type="button"
            className={`${String(seg.segOption)} ${source === 'own' ? String(seg.segOptionOn) : ''}`}
            aria-pressed={source === 'own'}
            onClick={() => {
              setSource('own');
            }}
          >
            <PencilIcon className={seg.segIcon} />
            {t('web:reports.editor.newQuestion.own')}
          </button>
          <button
            type="button"
            className={`${String(seg.segOption)} ${source === 'shelf' ? String(seg.segOptionOn) : ''}`}
            aria-pressed={source === 'shelf'}
            onClick={() => {
              setSource('shelf');
            }}
          >
            <BookmarkIcon className={seg.segIcon} filled />
            {t('web:reports.editor.newQuestion.fromShelf', { count: shelf.length })}
          </button>
        </div>
      </div>

      {source === 'shelf' ? (
        <ShelfPicker shelf={shelf} term={term} onTerm={setTerm} onPick={onPickShelf} t={t} />
      ) : (
        <QuestionFields
          value={value}
          onChange={setValue}
          allowClip={allowClip}
          clipsLeft={clipsLeft}
          t={t}
        />
      )}

      <div className={styles.newFoot}>
        {/* Plain text, no fill and no edge — the canon's own. A bordered pill beside the ember one
          * reads as two offers of equal weight where the export offers one. */}
        <button type="button" className={styles.newCancel} onClick={onCancel}>
          {t('web:reports.editor.newQuestion.cancel')}
        </button>
        {source === 'own' ? (
          <button
            type="button"
            className={`${String(ember.ember)} ${String(styles.newSubmit)}`}
            // `aria-disabled`, never `disabled`: the control keeps its place in the tab order and
            // its title says what is still missing.
            aria-disabled={complete ? undefined : true}
            title={complete ? undefined : t('web:reports.editor.newQuestion.incomplete')}
            onClick={submit}
          >
            {t('web:reports.editor.newQuestion.submit')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ShelfPicker({
  shelf,
  term,
  onTerm,
  onPick,
  t,
}: {
  readonly shelf: readonly ShelfQuestion[];
  readonly term: string;
  readonly onTerm: (term: string) => void;
  readonly onPick: (question: ShelfQuestion) => void;
  readonly t: TranslateKey;
}) {
  const found = filterShelf(shelf, { term, answerType: null, usage: 'all' });

  return (
    <div className={styles.field}>
      <SearchField
        value={term}
        onChange={onTerm}
        label={t('web:reports.editor.newQuestion.searchLabel')}
        placeholder={t('web:reports.editor.newQuestion.searchPlaceholder')}
        clearLabel={t('web:reports.editor.newQuestion.searchClear')}
        disabled={shelf.length === 0}
      />
      {found.length === 0 ? (
        <p className={styles.note}>
          {shelf.length === 0
            ? t('web:reports.editor.newQuestion.shelfEmpty')
            : t('web:reports.editor.newQuestion.shelfNoResults', { term })}
        </p>
      ) : (
        <div className={styles.shelfList}>
          {found.map((question) => (
            <button
              key={question.id}
              type="button"
              className={styles.shelfPick}
              onClick={() => {
                onPick(question);
              }}
            >
              <span className={styles.shelfPickLabel}>{question.label}</span>
              {answerTypeIcon(question.answerType, styles.typePillIcon)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
